<template>
  <component
    :is="cardWrapper.is"
    ref="cardRef"
    :class="['fieldset field card-form ']"
    v-bind="cardWrapper.props"
  >
    <div
      ref="cardFormWrapperRef"
      data-checkout-fields
      class="card-form__wrapper"
    >
      <div
        v-if="isHyperswitch"
        :class="['card-form__hyperswitch', { 'card-form__hyperswitch--pending': !isElementReady }]"
      >
        <CheckoutSkeleton
          v-if="!isElementReady"
          class="card-form__skeleton"
          :with-cta="false"
        />
        <PaymentInitFallback
          v-if="hasLagoIntentFailed"
          class="card-form__payment-notice"
          status="failed"
          :offline="!isOnline"
          @retry="refreshLagoIntent"
        />
        <PaymentInitFallback
          v-if="isSwitchSlow"
          class="card-form__payment-notice"
          status="failed"
          message="payment_update_slow_message"
          action="reload_page"
          @retry="reloadPage"
        />
        <PaymentElement
          ref="paymentElementRef"
          :client-secret="hyperswitchClientSecret"
          :sdk-authorization="hyperswitchSdkAuthorization"
          :appearance="hyperswitchAppearance"
          loader="never"
          :options="hyperswitchOptions"
          :height="hyperswitchElementHeight"
          :cardholder-name="nameOnCard"
          :return-url="resolveReturnUrl()"
          :locale="locale"
          data-testid="card-fields"
          @ready="onElementReady"
          @remount="onElementRemount"
          @switching="onElementSwitching"
          @settled="revealElement"
          @change="onHyperswitchChange"
          @walletclick="onWalletClick"
          @walletcomplete="onWalletComplete"
          @cancel="onWalletCancel"
          @focus="onHyperswitchFocus"
          @blur="onHyperswitchBlur"
        />
        <div
          ref="cardRowsRef"
          data-checkout-field="number"
          class="card-form__card-rows"
          :style="cardRowsTop === null ? undefined : { top: `${cardRowsTop}px` }"
        />
      </div>
      <template v-else>
        <component
          :is="ComponentNumber"
          class="ex3-input card-form__input"
          data-testid="card-number-input"
          data-checkout-field="number"
          :class="[errors.number ? 'border-red' : ' border-transparent']"
          :placeholder="$t('lp_v7_pop_up_card_number')"
          @change="onFieldChange('number', $event)"
          @focus="handleCardInputFocus('number')"
          @blur="handleCardInputBlur('number')"
        />
        <div class="card-form__small-inputs">
          <component
            :is="ComponentExpiry"
            class="ex3-input card-form__input"
            data-testid="card-expiry-input"
            data-checkout-field="expiry"
            :class="[errors.expiry ? 'border-red' : ' border-transparent']"
            :placeholder="$t('lp_v7_pop_up_card_due_date')"
            @change="onFieldChange('expiry', $event)"
            @focus="handleCardInputFocus('expiry')"
            @blur="handleCardInputBlur('expiry')"
          />
          <component
            :is="ComponentCvv"
            class="ex3-input card-form__input"
            data-testid="card-cvc-input"
            data-checkout-field="cvv"
            :class="[errors.cvv ? 'border-red' : ' border-transparent']"
            :placeholder="$t('lp_v7_pop_up_cvv_number')"
            @change="onFieldChange('cvv', $event)"
            @focus="handleCardInputFocus('cvv')"
            @blur="handleCardInputBlur('cvv')"
          />
        </div>
      </template>
      <div
        data-checkout-field="name"
        class="card-form__input"
        :class="[errors.name ? 'border-red' : ' border-transparent']"
      >
        <input
          v-model="nameOnCard"
          :disabled="dialogStore.loader"
          type="text"
          data-testid="card-name-input"
          :placeholder="$t('lp_v7_pop_up_card_name')"
          class="card-form__custom-input"
          :class="[{ 'card-form__custom-input-error': errors.name }]"
          @input="onInputName"
          @focus="handleCardInputFocus('name')"
          @blur="handleCardInputBlur('name')"
        />
      </div>
      <base-input
        v-if="withEmail"
        v-model="email"
        data-testid="checkout-email-input"
        icon-name="lock_icon"
        class="card-form__input-email"
        :placeholder="$t('enter_email_placeholder')"
        :error="showInputError || errors.email"
        @update:model-value="onInputEmail"
      />
      <div
        v-if="isCheckoutVariantA || isCheckoutVariantB"
        class="card-form__checkout-test-secure-payment"
      >
        <nuxt-icon
          name="lock-green"
          filled
        />
        <p>
          {{ $t('lp_checkout_variant_a_secure_payment') }}
        </p>
      </div>
    </div>
    <slot name="before-button" />
    <div class="px-32px">
      <base-button
        :type="superDiscount ? themeClasses.discount : themeClasses.regular"
        class="card-form__button"
        :class="{ 'card-form__button--checkout-test': isCheckoutVariantA || isCheckoutVariantB }"
        data-testid="checkout-next-btn"
        data-checkout-submit
        :is-disabled="isDisabledButton"
        :is-loading="dialogStore.loader"
        with-shadow
        @click="handlePayment"
        @disabled-click="isFormFilled"
      >
        {{ $t(checkoutButtonTextKey) }}
      </base-button>
    </div>
    <div
      v-if="isCheckoutVariantC || isCheckoutVariantD || isCheckoutVariantE"
      class="card-form__checkout-test-secure-payment card-form__checkout-test-secure-payment--below-button"
    >
      <nuxt-icon
        name="lock-green"
        filled
      />
      <p>
        {{ $t('lp_checkout_variant_a_secure_payment') }}
      </p>
    </div>
  </component>
</template>

<script lang="ts" setup>
import type { ComponentPublicInstance, PropType } from 'vue';
import { useNetwork, useTimeoutFn } from '@vueuse/core';
// @ts-expect-error — Chargebee package has no types
import {
  CardComponent as CbCardComponent,
  CardCvv as CbCardCvv,
  CardExpiry as CbCardExpiry,
  CardNumber as CbCardNumber,
} from '@chargebee/chargebee-js-vue-wrapper';
import {
  CardComponent as LagoCardComponent,
  CardNumber as LagoCardNumber,
  CardExpiry as LagoCardExpiry,
  CardCvv as LagoCardCvv,
} from '@appercut/lago-vue';
import { PaymentElement, type HyperElements } from '@appercut/round-vue';
import {
  HYPERSWITCH_CARD_ONLY_ELEMENT_HEIGHT,
  HYPERSWITCH_CARD_ROWS_HEIGHT,
  HYPERSWITCH_ELEMENT_HEIGHT,
  HYPERSWITCH_ELEMENT_OPTIONS,
} from '../common/constants/hyperswitchAppearance';
import { PAYMENT_SWITCH_SLOW_MS } from '../common/constants/paymentInit';
import { useHyperswitchRail } from '../composables/useHyperswitchRail';
import { useHyperswitchWalletButton } from '../composables/useHyperswitchWalletButton';
import { buildHyperswitchAppearance } from '../utils/hyperswitchAppearance';
import { buildCardOnlyWallets, buildHyperswitchWallets, isApplePayDevice } from '../utils/walletAvailability';

const gatewayStore = useKegelGatewayStore();
const isLago = computed(() => gatewayStore.provider === 'round');
const isHyperswitch = useHyperswitchRail();
const { locale, t } = useI18n();
const ComponentNumber = computed(() => isLago.value ? LagoCardNumber : CbCardNumber);
const ComponentExpiry = computed(() => isLago.value ? LagoCardExpiry : CbCardExpiry);
const ComponentCvv = computed(() => isLago.value ? LagoCardCvv : CbCardCvv);

const props = defineProps({
  themeClasses: {
    type: Object,
    default: () => ({ discount: 'red', regular: 'blue' }),
  },
  discountPrice: {
    type: Number,
    required: true,
  },
  roundedPrice: {
    type: Number,
    required: true,
  },
  chargebeeId: {
    type: String,
    required: true,
  },
  superDiscount: Boolean,
  upsale: Boolean,
  trial: Boolean,
  withEmail: Boolean,
  lagoPaymentIntent: {
    type: Object as PropType<Record<string, any>>,
    default: null,
  },
  hasLagoIntentFailed: Boolean,
  onWalletAuthorized: {
    type: Function as PropType<(paymentId: string | null) => Promise<boolean>>,
    default: null,
  },
  ensureLagoIntent: {
    type: Function as PropType<(name?: string) => Promise<Record<string, any> | null>>,
    default: null,
  },
  refreshLagoIntent: {
    type: Function as PropType<() => void>,
    default: null,
  },
  dropLagoIntent: {
    type: Function as PropType<() => void>,
    default: null,
  },
});

const emits = defineEmits(['success', 'buy-upsale', 'action', 'card-input-focus', 'card-input-blur']);
const {
  isCheckoutVariantA,
  isCheckoutVariantB,
  isCheckoutVariantC,
  isCheckoutVariantD,
  isCheckoutVariantE,
} = useCheckoutFormVariant();
const checkoutButtonTextKey = computed(() => {
  if (isCheckoutVariantC.value || isCheckoutVariantD.value || isCheckoutVariantE.value) {
    return 'whitening_checkout_continue';
  }

  return isCheckoutVariantA.value || isCheckoutVariantB.value
    ? 'lp_checkout_variant_a_buy_with_card'
    : 'lp_v7_pop_up_continue_button';
});

const cardFormWrapperRef = ref<HTMLElement | null>(null);

const {
  cardRef,
  errors,
  nameOnCard,
  fonts,
  styles,
  isDisabledButton,
  onReady,
  onChange,
  onHyperswitchChange,
  resetCardFields,
  sendStartedEvent,
  onWalletClick,
  onWalletComplete,
  onWalletCancel,
  onFieldChange,
  onInputName,
  handlePayment,
  isFormFilled,
  dialogStore,
  email,
  showInputError,
  onInputEmail,
} = useCreditCardForm({
  discountPrice: toRef(props, 'discountPrice'),
  roundedPrice: toRef(props, 'roundedPrice'),
  chargebeeId: toRef(props, 'chargebeeId'),
  upsale: toRef(props, 'upsale'),
  withEmail: props.withEmail,
  lagoPaymentIntent: toRef(props, 'lagoPaymentIntent'),
  ensureLagoIntent: props.ensureLagoIntent ?? undefined,
  dropLagoIntent: props.dropLagoIntent ?? undefined,
  onSuccess: (id: number) => emits('success', id),
  onAction: () => emits('action'),
  clearCardFields: () => {
    if (!isHyperswitch.value) return;

    paymentElementRef.value?.remount?.();
  },
  onBuyUpsale: payload => emits('buy-upsale', payload),
  onWalletAuthorized: async paymentId => (await props.onWalletAuthorized?.(paymentId)) ?? false,
});

const cardWrapper = computed(() => {
  if (isHyperswitch.value) return { is: 'div', props: {} };

  return {
    is: isLago.value ? LagoCardComponent : CbCardComponent,
    props: { styles: styles.value, fonts: fonts.value, onReady, onChange },
  };
});

const hyperswitchClientSecret = computed(() => props.lagoPaymentIntent?.client_secret ?? '');
const hyperswitchSdkAuthorization = computed(() => props.lagoPaymentIntent?.sdk_authorization ?? '');
const { resolveReturnUrl } = useCheckoutReturnUrl();
const isElementReady = ref(false);

const ELEMENT_SETTLE_FALLBACK_MS = 6_000;
let settleFallback: ReturnType<typeof setTimeout> | null = null;

function armSettleFallback(): void {
  if (settleFallback) clearTimeout(settleFallback);

  settleFallback = setTimeout(revealElement, ELEMENT_SETTLE_FALLBACK_MS);
}

function revealElement(): void {
  if (settleFallback) {
    clearTimeout(settleFallback);
    settleFallback = null;
  }

  isElementReady.value = true;
}

function onElementReady(api: unknown): void {
  armSettleFallback();
  onReady(api);
  if (hasSeparateWalletButton.value) walletButton.mountFrom(paymentElementRef.value?.elements);
}

function onElementRemount(): void {
  resetCardFields();
  isElementReady.value = false;
  armSettleFallback();
}

const { isOnline } = useNetwork();

const isSwitchSlow = ref(false);
const slowSwitchTimer = useTimeoutFn(() => {
  isSwitchSlow.value = true;
}, PAYMENT_SWITCH_SLOW_MS, { immediate: false });

function onElementSwitching(isSwitching: boolean): void {
  slowSwitchTimer.stop();
  isSwitchSlow.value = false;
  if (isSwitching) slowSwitchTimer.start();
}

function reloadPage(): void {
  reloadNuxtApp();
}

onBeforeUnmount(() => {
  if (settleFallback) clearTimeout(settleFallback);
});
const hasSeparateWalletButton = computed(() => isHyperswitch.value && isCheckoutVariantD.value);
const hyperswitchElementHeight = computed(() => hasSeparateWalletButton.value
  ? HYPERSWITCH_CARD_ONLY_ELEMENT_HEIGHT
  : HYPERSWITCH_ELEMENT_HEIGHT);

const hyperswitchAppearance = computed(() => buildHyperswitchAppearance(t));

const hyperswitchOptions = computed(() => {
  return {
    ...HYPERSWITCH_ELEMENT_OPTIONS,
    layout: { ...HYPERSWITCH_ELEMENT_OPTIONS.layout, separatorText: t('lp_v7_pop_up_or_pay_with_card') },
    wallets: hasSeparateWalletButton.value
      ? buildCardOnlyWallets(resolveReturnUrl())
      : buildHyperswitchWallets(resolveReturnUrl(), isApplePayDevice()),
  };
});

const walletButton = useHyperswitchWalletButton({
  wallets: () => buildHyperswitchWallets(resolveReturnUrl(), isApplePayDevice()),
  onComplete: onWalletComplete,
  onClick: onWalletClick,
  onCancel: onWalletCancel,
});

const paymentElementRef = ref<(ComponentPublicInstance & {
  elements?: HyperElements | null
  remount?: () => void
}) | null>(null);
const cardRowsRef = ref<HTMLElement | null>(null);
const cardRowsTop = ref<number | null>(null);

function onHyperswitchFocus(): void {
  sendStartedEvent();
  if (cardRowsTop.value === null && paymentElementRef.value) {
    cardRowsTop.value = paymentElementRef.value.$el.offsetHeight - HYPERSWITCH_CARD_ROWS_HEIGHT;
  }
  emits('card-input-focus', cardRowsRef.value);
}

function onHyperswitchBlur(): void {
  emits('card-input-blur', cardRowsRef.value);
}

function handleCardInputFocus(field: string): void {
  const element = cardFormWrapperRef.value?.querySelector<HTMLElement>(`[data-checkout-field="${field}"]`) ?? null;
  emits('card-input-focus', element);
}
function handleCardInputBlur(field: string): void {
  const element = cardFormWrapperRef.value?.querySelector<HTMLElement>(`[data-checkout-field="${field}"]`) ?? null;
  emits('card-input-blur', element);
}
</script>

<style lang="scss" scoped>
.card-form__hyperswitch {
  position: relative;

  &--pending {
    min-height: 245px;
  }
}

.card-form__skeleton,
.card-form__payment-notice {
  position: absolute;
  inset: 0;
  z-index: 1;
  background: #fff;
}

.card-form__card-rows {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  pointer-events: none;
}

.card-form__payment-notice {
  justify-content: center;
}

  .card-form {
    width: 100%;

    &__wrapper {
      display: grid;
      grid-template-columns: repeat(1, minmax(0, 1fr));
      gap: 8px;
      width: 100%;
    }

    &__small-inputs {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 8px;
    }

    &__input {
      padding: 16px;
      border-radius: 9px;
      border-width: 2px;
      border-style: solid;
      background: $gray-bg;
      box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.04) inset;
      border-color: transparent;
      max-height: 55px;

      color: $text-color;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 1.35;

      & input {
        &::placeholder {
          font-family: "Roboto", sans-serif;
          color: $disclaimer-text;
          font-weight: 400;
        }

        &:focus-visible {
          border: none;
          outline: none;
        }

        &:focus {
          border: none;
          outline: none;
        }
      }
    }

    &__input-email {
      border: none;
      width: 100%;
      padding: 0 2px;
      margin-top: 16px;
      height: 55px;

      & :deep(.base-input__input) {
        &::placeholder {
          font-family: "Roboto", sans-serif;
          color: $disclaimer-text;
          font-weight: 400;
        }
      }
    }

    &__custom-input {
      background-color: transparent;
      border: none;
      width: 100%;
      padding: 0 2px;
    }

    &__custom-input-error {
      color: $red;
    }

    &__button {
      font-weight: 700;
      margin-top: 24px;
      width: 100%;
      border-radius: 100px;
      padding: 18px 0;
      max-height: 57px;

      &.card-form__button--checkout-test.base-button__disabled {
        border-radius: 100px;
        opacity: 0.5;
        background: $blue;
      }
    }

    &__checkout-test-secure-payment {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 3px;

      & :deep(.nuxt-icon){
        font-size: 13px;
      }

      & p {
        color: rgba(17, 17, 19, 0.50);
        font-family: Roboto, sans-serif;
        font-size: 12px;
        font-style: normal;
        font-weight: 400;
        line-height: 150%;
      }

      &--below-button {
        margin-top: 8px;
      }
    }
  }

  .border-red {
    border-color: $red;
  }
</style>
